import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Posting } from '../models/posting.model';
import { PostingStateModel } from '../models/posting.state-model';
import { PostingApiService } from '../services/posting.api-service';
import {
    AddPostings,
    CreatePosting,
    CreatePostingFailure,
    CreatePostingFromArticle,
    CreatePostingFromArticleFailure,
    CreatePostingFromArticleSuccess,
    CreatePostingSuccess,
    DeletePosting,
    DeletePostingFailure,
    DeletePostingSuccess,
    FetchEngageCategories,
    FetchEngageCategoriesFailure,
    FetchEngageCategoriesSuccess,
    FetchEngageCategoriesWithPostings,
    FetchEngageCategoriesWithPostingsFailure,
    FetchEngageCategoriesWithPostingsSuccess,
    FetchPosting,
    FetchPostingFailure,
    FetchPostings,
    FetchPostingsFailure,
    FetchPostingsSuccess,
    FetchPostingSuccess,
    SetPosting,
    SetPostings,
    UnsetPosting,
    UpdatePosting,
    UpdatePostingFailure,
    UpdatePostingFromArticle,
    UpdatePostingFromArticleFailure,
    UpdatePostingFromArticleSuccess,
    UpdatePostingSuccess,
} from '../actions';
import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { IEntityState } from 'app/projects/entity/src/lib/interfaces/entity.state.interface';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';

@State<PostingStateModel>({
    name: 'postings',
    defaults: new PostingStateModel(),
})
@Injectable()
export class PostingState extends EntityState<Posting> implements IEntityState<Posting> {
    @Selector()
    static getEntity(state: PostingStateModel, id: string = state.item): Posting {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntitiesAsMap(state: PostingStateModel): Map<string, Posting> {
        if (!state.map) {
            return null;
        }
        return state.map;
    }

    @Selector()
    static getEntities(state: PostingStateModel): Posting[] {
        if (!state.map) {
            return null;
        }

        return Array.from(state.map.values());
    }

    constructor(private _postingApiService: PostingApiService) {
        super(_postingApiService, 0);

        this._propstoSearch = Posting.props_to_search;
    }

    @Action(AddPostings)
    addEntities(ctx: StateContext<PostingStateModel>, action: AddPostings): void {
        this._addEntities(ctx, action);
    }

    @Action(CreatePosting)
    createEntity(ctx: StateContext<PostingStateModel>, action: CreatePosting): void {
        this._createEntity(ctx, action, CreatePostingSuccess, CreatePostingFailure, AddPostings).then(() => {});
    }

    @Action(SetPosting)
    setEntity(ctx: StateContext<PostingStateModel>, action: SetPosting): void {
        this._setEntity(ctx, action, AddPostings);
    }

    @Action(DeletePosting)
    deleteEntity(ctx: StateContext<PostingStateModel>, action: DeletePosting): void {
        this._deleteEntity(ctx, action, DeletePostingSuccess, DeletePostingFailure).then(() => {});
    }

    @Action(FetchPosting)
    fetchEntity(ctx: StateContext<PostingStateModel>, action: FetchPosting): void {
        this._fetchEntity(ctx, action, FetchPostingSuccess, FetchPostingFailure, AddPostings, SetPosting).then(() => {});
    }

    @Action(FetchPostingSuccess)
    fetchEntitySuccess(ctx: StateContext<PostingStateModel>, action: FetchPostingSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchPostingFailure)
    fetchEntityFailure(ctx: StateContext<PostingStateModel>, action: FetchPostingFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(FetchPostings)
    fetchEntities(ctx: StateContext<PostingStateModel>, SetEntityAction: any, SuccessAction: any, FailureAction: any): void {
        this._fetchEntities(ctx, FetchPostingSuccess, FetchPostingFailure, SetPosting).then(() => {});
    }

    @Action(FetchPostingsFailure)
    fetchEntitiesFailure(ctx: StateContext<PostingStateModel>): void {
        this._fetchEntitiesFailure(ctx);
    }

    @Action(FetchPostingsSuccess)
    fetchEntitiesSuccess(ctx: StateContext<PostingStateModel>): void {
        this._fetchEntitiesSuccess(ctx);
    }

    @Action(SetPostings)
    setEntities(ctx: StateContext<PostingStateModel>, action: SetPostings): void {
        this._setEntities(ctx, action);
    }

    @Action(UnsetPosting)
    unsetEntity(ctx: StateContext<PostingStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(UpdatePosting)
    updateEntity(ctx: StateContext<PostingStateModel>, action: UpdatePosting): void {
        this._updateEntity(ctx, action, UpdatePostingSuccess, UpdatePostingFailure, AddPostings).then(() => {});
    }

    @Action(CreatePostingFromArticle)
    createPostingFromArticle({ dispatch }: StateContext<PostingStateModel>, { payload }: CreatePostingFromArticle): void {
        this._postingApiService.createPostingFromArticle(payload).subscribe(
            (entity) => {
                dispatch(new CreatePostingFromArticleSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                dispatch(new CreatePostingFromArticleFailure({ response }));
            }
        );
    }

    @Action(FetchEngageCategories)
    fetchEngageCategories({ dispatch }: StateContext<PostingStateModel>, { payload }: FetchEngageCategories): void {
        this._postingApiService
            .fetchEngageCategories(payload.audienceId, payload.searchTerm, payload.postingTypeId, {
                sectionId: payload.sectionId,
                courseOfferingId: payload.courseOfferingId,
            })
            .subscribe(
                (entities) => {
                    dispatch(new FetchEngageCategoriesSuccess(entities));
                },
                (response: HttpErrorResponse) => {
                    dispatch(new FetchEngageCategoriesFailure());
                }
            );
    }

    @Action(FetchEngageCategoriesWithPostings)
    fetchEngageCategoriesWithPostings({ dispatch }: StateContext<PostingStateModel>, { payload }: FetchEngageCategoriesWithPostings): void {
        this._postingApiService
            .fetchEngageCategoriesWithPostings(payload.audienceId, {
                sectionId: payload.sectionId,
                courseOfferingId: payload.courseOfferingId,
            })
            .subscribe(
                (entities) => {
                    dispatch(new FetchEngageCategoriesWithPostingsSuccess(entities));
                },
                (response: HttpErrorResponse) => {
                    dispatch(new FetchEngageCategoriesWithPostingsFailure());
                }
            );
    }

    @Action(UpdatePostingFromArticle)
    updatePostingFromArticle({ dispatch }: StateContext<PostingStateModel>, { payload }: UpdatePostingFromArticle): void {
        this._postingApiService.updatePostingFromArticle(payload).subscribe(
            (entity) => {
                dispatch(new UpdatePostingFromArticleSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                dispatch(new UpdatePostingFromArticleFailure({ response }));
            }
        );
    }
}
